import { useEffect } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { MessageCircle } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { useConsent, track, captureAttribution } from "../lib/store";
import { waLink } from "../lib/format";
import ChatWidget from "./ChatWidget";
import { ThemeProvider } from "../lib/theme";
import ErrorBoundary from "./ErrorBoundary";
import { Helmet } from "react-helmet-async";
import { mediaUrl } from "../lib/api";

function OrgJsonLd() {
  const { settings: s } = useSite();
  if (!s) return null;
  const site = window.location.origin;
  const ld = {
    "@context": "https://schema.org", "@type": "RealEstateAgent", name: s.company_name_en, alternateName: s.company_name_ar, url: site,
    logo: s.logo_url ? mediaUrl(s.logo_url) : `${site}/favicon.ico`, telephone: s.phone, email: s.email,
    taxID: s.vat_number || undefined, vatID: s.vat_number || undefined,
    identifier: s.cr_number ? { "@type": "PropertyValue", propertyID: "Commercial Registration (C.R.)", value: s.cr_number } : undefined,
    address: { "@type": "PostalAddress", addressCountry: "SA" },
    contactPoint: [s.phone && { "@type": "ContactPoint", telephone: s.phone, contactType: "customer service", areaServed: "SA", availableLanguage: ["en", "ar"] },
      ...(s.mobiles || []).slice(0, 1).map((m) => ({ "@type": "ContactPoint", telephone: m, contactType: "sales", areaServed: "SA" })),
      ...(s.department_emails || []).filter((d) => d.in_footer).map((d) => ({ "@type": "ContactPoint", email: d.email, contactType: d.label_en }))].filter(Boolean),
    sameAs: Object.values(s.socials || {}).filter((u) => u && !(s.placeholders || {}).socials),
  };
  return <Helmet><script type="application/ld+json">{JSON.stringify(ld)}</script></Helmet>;
}

function CookieBanner() {
  const { t } = useI18n();
  const [consent, setConsent] = useConsent();
  return (
    <AnimatePresence>
      {consent === null && (
        <motion.div role="dialog" aria-live="polite" aria-label="Cookie consent" data-testid="cookie-banner" initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          className="on-media glass-strong fixed inset-x-4 bottom-4 z-[60] mx-auto flex max-w-3xl flex-col gap-4 rounded-sm p-5 shadow-2xl sm:flex-row sm:items-center">
          <p className="flex-1 text-sm text-fg/80">{t("cookie.text")} <Link to="/privacy" className="text-cta underline-offset-4 hover:underline">{t("cookie.learn")}</Link></p>
          <div className="flex gap-2">
            <button type="button" data-testid="cookie-reject-button" onClick={() => setConsent("rejected")} className="btn-ghost !px-5 !py-2">{t("cookie.reject")}</button>
            <button type="button" data-testid="cookie-accept-button" onClick={() => setConsent("accepted")} className="btn-cta !px-5 !py-2">{t("cookie.accept")}</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function WhatsAppFloat() {
  const { settings } = useSite();
  const { t, tr } = useI18n();
  const { pathname } = useLocation();
  if (!settings?.whatsapp || pathname.startsWith("/properties/") && pathname.split("/").length > 2 && !["for-sale", "for-rent"].includes(pathname.split("/")[2])) return null;
  return (
    <a href={waLink(settings.whatsapp, tr(settings, "company_name"))} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float-button" aria-label={t("contact.whatsapp")}
      className="fixed bottom-[5.5rem] end-6 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-brand-2 text-fg shadow-xl ring-1 ring-cta/40 transition-transform duration-300 hover:scale-110">
      <MessageCircle className="h-6 w-6" />
    </a>
  );
}

export default function Layout() {
  return <ThemeProvider><LayoutInner /></ThemeProvider>;
}

function LayoutInner() {
  const { pathname } = useLocation();
  const { t, isRtl } = useI18n();
  useEffect(() => { captureAttribution(); }, []);
  useEffect(() => { window.scrollTo(0, 0); track("page_view"); }, [pathname]);
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-[70] focus:rounded focus:bg-cta focus:px-4 focus:py-2 focus:text-canvas">{t("nav.skip")}</a>
      <Navbar />
      <main id="main" className="flex-1"><ErrorBoundary key={pathname}><Outlet /></ErrorBoundary></main>
      <Footer />
      <OrgJsonLd />
      <WhatsAppFloat />
      <ChatWidget />
      <CookieBanner />
      <Toaster theme="dark" position={isRtl ? "top-left" : "top-right"} richColors />
    </div>
  );
}
